import React from "react";
import { Icon as DSIcon, Card, Button, Badge, Avatar, IconBtn } from "../../design-system";
import {
  CAT,
  STATUS,
  PRIO,
  ASTATUS,
  ASSET_TYPE,
  RES_TYPE,
  assetById,
  resourceById,
  userById,
  ticketsForUser,
} from "./data";

// =====================================================================
//  Icon — wrapper de porte
//  El prototipo de diseño usa nombres de icono en minúscula (estilo
//  Remixicon) y la prop `stroke` para el color. El DS del repo expone
//  `lucide-react` (PascalCase) y colorea vía currentColor. Este wrapper
//  traduce el nombre y mapea `stroke` -> color, para que el cuerpo de
//  las vistas se mantenga fiel al diseño original.
//
//  Nota: las primitivas del DS (Button/IconBtn/Input/EmptyState) reciben
//  el nombre Lucide directamente (su contrato), no pasan por aquí.
// =====================================================================
const ICONS = {
  alert: "AlertTriangle", arrowLeft: "ArrowLeft", arrowRight: "ArrowRight",
  barChart: "BarChart3", bookOpen: "BookOpen", building: "Building2",
  calendar: "Calendar", check: "Check", checkCircle: "CheckCircle2",
  chevronDown: "ChevronDown", chevronLeft: "ChevronLeft", chevronRight: "ChevronRight",
  clock: "Clock", cloud: "Cloud", document: "FileText", download: "Download",
  edit: "Pencil", externalLink: "ExternalLink", eye: "Eye", fileText: "FileText",
  filter: "Filter", folder: "Folder", hash: "Hash", headphones: "Headphones",
  help: "HelpCircle", history: "History", inbox: "Inbox", key: "Key",
  keyboard: "Keyboard", laptop: "Laptop", lifebuoy: "LifeBuoy", link: "Link2",
  lock: "Lock", mail: "Mail", mapPin: "MapPin", message: "MessageSquare",
  monitor: "Monitor", more: "MoreHorizontal", mouse: "Mouse", package: "Package",
  paperclip: "Paperclip", phone: "Phone", plus: "Plus", printer: "Printer",
  refresh: "RefreshCw", search: "Search", send: "Send", server: "Server",
  smartphone: "Smartphone", tag: "Tag", ticket: "Ticket", upload: "Upload",
  userMinus: "UserMinus", userPlus: "UserPlus", users: "Users", wrench: "Wrench",
  x: "X", zap: "Zap",
};

export function Icon({ name, size, stroke, style, ...rest }) {
  const mapped = ICONS[name] || name;
  const merged = stroke ? { color: stroke, ...(style || {}) } : style;
  return <DSIcon name={mapped} size={size} style={merged} {...rest} />;
}

// =====================================================================
//  Badges / chips de estado (antes en SoporteData)
// =====================================================================
export function StatusBadge({ status, variant = "soft" }) {
  const m = STATUS[status] || STATUS.nuevo;
  return <Badge tone={m.tone} variant={variant} dot={m.dot}>{m.label}</Badge>;
}
export function PriorityBadge({ priority, variant = "soft" }) {
  const m = PRIO[priority] || PRIO.media;
  return <Badge tone={m.tone} variant={variant} dot>{m.label}</Badge>;
}
export function AssetStatusBadge({ status, variant = "soft" }) {
  const m = ASTATUS[status] || ASTATUS.disponible;
  return <Badge tone={m.tone} variant={variant} dot>{m.label}</Badge>;
}

// Chip de categoría: tile con icono + etiqueta opcional
const CAT_TONES = {
  info: { bg: "var(--kr-status-info-bg)", fg: "var(--kr-status-info-fg)" },
  pending: { bg: "var(--kr-status-pending-bg)", fg: "var(--kr-status-pending-fg)" },
  brand: { bg: "var(--kr-action-soft-bg)", fg: "var(--kr-action-soft-fg)" },
  warning: { bg: "var(--kr-status-warning-bg)", fg: "var(--kr-status-warning-fg)" },
  neutral: { bg: "var(--kr-status-neutral-bg)", fg: "var(--kr-status-neutral-fg)" },
  success: { bg: "var(--kr-status-success-bg)", fg: "var(--kr-status-success-fg)" },
};
export function CatIcon({ cat, size = 28, iconSize }) {
  const m = CAT[cat] || CAT.otros;
  const tones = CAT_TONES[m.tone] || { bg: "var(--kr-neutral-100)", fg: "var(--kr-fg-muted)" };
  return (
    <span style={{ width: size, height: size, borderRadius: 8, background: tones.bg, color: tones.fg, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon name={m.icon} size={iconSize || Math.round(size * 0.5)} />
    </span>
  );
}
export function CatChip({ cat }) {
  const m = CAT[cat] || CAT.otros;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--kr-fg-strong)", fontWeight: 500 }}>
      <Icon name={m.icon} size={13} stroke="var(--kr-fg-muted)" />{m.label}
    </span>
  );
}

// Tile de categoría a partir de datos de la API (categoría configurable):
// `icon` y `color`(tono) vienen del catálogo del backend. Si faltan, cae a
// un tile neutro con icono genérico.
export function CategoryIcon({ icon, color, size = 28, iconSize }) {
  const tones = CAT_TONES[color] || { bg: "var(--kr-neutral-100)", fg: "var(--kr-fg-muted)" };
  return (
    <span style={{ width: size, height: size, borderRadius: 8, background: tones.bg, color: tones.fg, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon name={icon || "help"} size={iconSize || Math.round(size * 0.5)} />
    </span>
  );
}

// =====================================================================
//  Helpers presentacionales (antes en SoporteUI)
// =====================================================================

// Cabecera de página (eyebrow + título display + subtítulo + acciones)
export function SupHeader({ eyebrow, title, subtitle, actions, onBack, backLabel }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
      <div style={{ minWidth: 0 }}>
        {onBack && (
          <button onClick={onBack} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "transparent", color: "var(--kr-fg-muted)", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: 8 }}>
            <Icon name="arrowLeft" size={14} />{backLabel || "Volver"}
          </button>
        )}
        {eyebrow && <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 4 }}>{eyebrow}</div>}
        <h1 style={{ margin: 0, fontFamily: "var(--kr-font-display)", fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--kr-fg-strong)" }}>{title}</h1>
        {subtitle && <p style={{ margin: "6px 0 0", fontSize: 14, color: "var(--kr-fg-muted)" }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>{actions}</div>}
    </div>
  );
}

// Título de sección dentro de cards
export function SupSection({ eyebrow, title, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
      <div>
        {eyebrow && <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{eyebrow}</div>}
        <div style={{ fontSize: 16, fontWeight: 600, color: "var(--kr-fg-strong)", marginTop: eyebrow ? 4 : 0 }}>{title}</div>
      </div>
      {right}
    </div>
  );
}

// Mini KPI card
export function SupKpi({ label, value, unit, hint, hintTone, icon, onClick, accent }) {
  return (
    <Card onClick={onClick} style={onClick ? { transition: "box-shadow .15s, border-color .15s" } : {}}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <span style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{label}</span>
        {icon && <Icon name={icon} size={16} stroke={accent || "var(--kr-fg-muted)"} />}
      </div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontVariantNumeric: "tabular-nums", fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em", color: accent || "var(--kr-fg-strong)", lineHeight: 1.1 }}>
        {value}{unit && <span style={{ fontSize: 13, color: "var(--kr-fg-muted)", marginLeft: 4 }}>{unit}</span>}
      </div>
      {hint && <div style={{ marginTop: 8, fontSize: 11, fontFamily: "var(--kr-font-mono)", color: hintTone === "down" ? "var(--kr-danger-700)" : hintTone === "up" ? "var(--kr-success-700)" : "var(--kr-fg-muted)" }}>{hint}</div>}
    </Card>
  );
}

// Barras horizontales (distribución por categoría / estado / etc.)
export function SupBars({ data, accent }) {
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {data.map((d, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "150px 1fr 40px", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 12, color: "var(--kr-fg-primary)", display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0 }}>
            {d.icon && <Icon name={d.icon} size={13} stroke="var(--kr-fg-muted)" />}
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{d.label}</span>
          </span>
          <div style={{ height: 8, background: "var(--kr-neutral-100)", borderRadius: 999, overflow: "hidden" }}>
            <div style={{ width: `${(d.value / max) * 100}%`, height: "100%", background: d.color || accent || "var(--kr-action)", borderRadius: 999 }} />
          </div>
          <span style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12, color: "var(--kr-fg-strong)", fontWeight: 600, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{d.value}</span>
        </div>
      ))}
    </div>
  );
}

// Mini resumen de activo (contextual, nunca central)
export function AssetMini({ assetId, onOpen, compact }) {
  const a = assetById(assetId);
  if (!a) return null;
  return (
    <div onClick={onOpen ? () => onOpen(a.id) : undefined} style={{
      display: "flex", alignItems: "center", gap: 12, padding: compact ? "10px 12px" : 12,
      border: "1px solid var(--kr-border-subtle)", borderRadius: 10, background: "var(--kr-neutral-25)",
      cursor: onOpen ? "pointer" : "default",
    }}>
      <span style={{ width: 38, height: 38, borderRadius: 8, background: "#fff", border: "1px solid var(--kr-border)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--kr-fg-muted)", flexShrink: 0 }}>
        <Icon name={ASSET_TYPE[a.type] || "package"} size={18} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.brand} {a.model}</div>
        <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>{a.code} · {a.type}</div>
      </div>
      <AssetStatusBadge status={a.status} />
      {onOpen && <Icon name="chevronRight" size={15} stroke="var(--kr-fg-subtle)" />}
    </div>
  );
}

// Pill de recurso
export function ResourcePill({ resId, onOpen }) {
  const r = resourceById(resId);
  if (!r) return null;
  const t = RES_TYPE[r.type];
  return (
    <span onClick={onOpen ? () => onOpen(r.id) : undefined} style={{
      display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 10px",
      border: "1px solid var(--kr-border)", borderRadius: 999, background: "#fff",
      fontSize: 12, color: "var(--kr-fg-strong)", fontWeight: 500, cursor: onOpen ? "pointer" : "default",
    }}>
      <Icon name={t.icon} size={13} stroke="var(--kr-fg-muted)" />{r.name}
    </span>
  );
}

// Fila compacta de ticket (listas de recientes)
export function TicketMiniRow({ ticket, onOpen, showUser }) {
  return (
    <div onClick={onOpen ? () => onOpen(ticket.code) : undefined} style={{
      display: "flex", alignItems: "center", gap: 10, padding: "9px 0", cursor: onOpen ? "pointer" : "default",
    }}>
      <CatIcon cat={ticket.cat} size={26} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "var(--kr-fg-strong)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ticket.title}</div>
        <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>
          {ticket.code}{showUser && userById(ticket.user) ? ` · ${userById(ticket.user).name}` : ""} · {ticket.updated}
        </div>
      </div>
      <StatusBadge status={ticket.status} />
    </div>
  );
}

// Panel contextual de usuario (equipos + recursos + tickets)
// El núcleo del "Help Desk con contexto de inventario".
export function UserContextPanel({ userId, onOpenAsset, onOpenUser, onOpenTicket, currentTicketCode, title = "Contexto del usuario" }) {
  const u = userById(userId);
  if (!u) return (
    <div style={{ padding: 20, textAlign: "center", color: "var(--kr-fg-muted)", fontSize: 13 }}>
      Selecciona un usuario para ver su contexto de soporte.
    </div>
  );
  const tickets = ticketsForUser(userId).filter((t) => t.code !== currentTicketCode).slice(0, 4);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {/* identidad */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Avatar name={u.name} size={44} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{u.name}</div>
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>{u.role} · {u.dept}</div>
        </div>
        {onOpenUser && <IconBtn icon="ExternalLink" title="Abrir ficha de usuario" onClick={() => onOpenUser(u.id)} />}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, fontSize: 12, color: "var(--kr-fg-muted)" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="building" size={12} />{u.company}</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="mail" size={12} />{u.email}</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="phone" size={12} />{u.phone}</span>
      </div>

      {/* equipos */}
      <div>
        <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 8 }}>Equipos asignados ({u.assets.length})</div>
        {u.assets.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {u.assets.map((aid) => <AssetMini key={aid} assetId={aid} onOpen={onOpenAsset} compact />)}
          </div>
        ) : <div style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>Sin equipos asignados.</div>}
      </div>

      {/* recursos */}
      <div>
        <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 8 }}>Recursos y licencias ({u.resources.length})</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {u.resources.map((rid) => <ResourcePill key={rid} resId={rid} />)}
        </div>
      </div>

      {/* tickets recientes */}
      <div>
        <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600, marginBottom: 2 }}>Últimos tickets del usuario</div>
        {tickets.length ? tickets.map((t, i) => (
          <div key={t.code} style={{ borderTop: i === 0 ? "none" : "1px solid var(--kr-border-subtle)" }}>
            <TicketMiniRow ticket={t} onOpen={onOpenTicket} />
          </div>
        )) : <div style={{ fontSize: 12, color: "var(--kr-fg-subtle)", marginTop: 8 }}>Sin tickets anteriores.</div>}
      </div>
    </div>
  );
}

// Select estilizado (idéntico look al de inputs del prototipo)
export function SupSelect({ value, onChange, options, placeholder, style = {} }) {
  return (
    <div style={{ position: "relative", ...style }}>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{
        appearance: "none", WebkitAppearance: "none", width: "100%", height: 36,
        border: "1px solid var(--kr-border)", borderRadius: 6, background: "#fff",
        padding: "0 32px 0 12px", fontSize: 13, color: value ? "var(--kr-fg-strong)" : "var(--kr-fg-subtle)",
        fontFamily: "var(--kr-font-sans)", cursor: "pointer", outline: "none",
      }}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <Icon name="chevronDown" size={14} stroke="var(--kr-fg-muted)" style={{ position: "absolute", right: 10, top: 11, pointerEvents: "none" }} />
    </div>
  );
}

// Chip de filtro (toggle pill)
export function FilterChip({ active, onClick, children, icon }) {
  return (
    <button onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 12px",
      border: `1px solid ${active ? "var(--kr-action)" : "var(--kr-border)"}`,
      background: active ? "var(--kr-action-soft-bg)" : "#fff",
      color: active ? "var(--kr-action-soft-fg)" : "var(--kr-fg-primary)",
      borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
    }}>
      {icon && <Icon name={icon} size={13} />}{children}
    </button>
  );
}

// Empty state del módulo
export function SupEmpty({ icon = "inbox", title, hint }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", textAlign: "center", gap: 6 }}>
      <span style={{ width: 48, height: 48, borderRadius: 12, background: "var(--kr-neutral-100)", color: "var(--kr-fg-subtle)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 6 }}>
        <Icon name={icon} size={22} />
      </span>
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{title}</div>
      {hint && <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", maxWidth: 320 }}>{hint}</div>}
    </div>
  );
}
